import styled from 'styled-components';

import { ACQ, ANIM, COLORS, GRADIENTS, HOME, SPACE, WEBSITE_ANALYTICS } from '../../../../theme/tokens';
import { thinScrollbar } from '../../../../theme/mixins';
import { Divider } from '../../../../components';

/** The desktop rail's width: room for a filter's name and a short choice (a long one ellipsizes). */
const RAIL_WIDTH = '232px';
/** The split's fixed height: a long list of choices scrolls inside it, the sheet stays put. */
const SPLIT_HEIGHT = 'min(60vh, 520px)';

/** The filters pill: the period pill's shape, so the header keeps its look; `$active` (filters applied) in the
 *  studio's applied-filter orange. */
export const Pill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-height: 48px;
  margin-left: auto;
  padding: ${SPACE[0]} 14px; /* off-scale on purpose: the period pill's inset */
  color: ${(p) => (p.$active ? ACQ.orange : HOME.t50)};
  border: 2px solid ${(p) => (p.$active ? ACQ.orangeBorder : HOME.intervalBorder)};
  background: ${(p) => (p.$active ? ACQ.orangeTint : 'transparent')};
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

/** A filter's own pill: the filters pill's shape, without pushing itself to the right. */
export const ShortcutPillButton = styled(Pill)`
  margin-left: ${SPACE[0]};
  max-width: 280px;
`;

export const ShortcutLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** How many filters are on, beside the pill's label. */
export const Count = styled.span`
  min-width: 20px;
  height: 20px;
  padding: ${SPACE[0]} 6px;
  display: inline-grid;
  place-items: center;
  border-radius: 999px;
  background: ${ACQ.orange};
  color: ${COLORS.pageBg};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

/* ── desktop: the filters in a rail, the one shown in the pane beside it ─────────────────────────────────── */

/** The rail and the pane, at one fixed height: switching filters never resizes the sheet (each side scrolls). The
 *  split mirrors the creatives editor's (EditCreativeSheet): a list column, a hairline, the detail. */
export const Split = styled.div`
  display: grid;
  grid-template-columns: ${RAIL_WIDTH} minmax(0, 1fr);
  height: ${SPLIT_HEIGHT};
  min-height: 0;
`;

export const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-height: 0;
  overflow-y: auto;
  padding-right: ${SPACE[12]};
  ${thinScrollbar}
`;

/** One filter in the rail: its name over its current choice; the one shown in the pane in the menus' picked fill. */
export const RailItem = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[2]};
  width: 100%;
  padding: ${SPACE[12]} ${SPACE[12]};
  border: 0;
  border-radius: 10px;
  background: ${(p) => (p.$selected ? HOME.menuActiveBg : 'transparent')};
  color: ${HOME.t50};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${HOME.ease};

  &:hover {
    background: ${(p) => (p.$selected ? HOME.menuActiveBg : HOME.menuHoverBg)};
  }
`;

export const RailName = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

/** A filter's current choice: muted while it is the default, in the applied-filter orange once one is picked. */
export const RailValue = styled.span<{ $applied: boolean }>`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: ${(p) => (p.$applied ? ACQ.orange : HOME.t400)};
`;

export const Pane = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-left: ${SPACE[12]};
  border-left: 1px solid ${COLORS.hairline};
  ${thinScrollbar}
`;

/* ── phone: the filters as a list, drilling into one ─────────────────────────────────────────────────────── */

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** A filter on the phone's list: its name, its current choice and the drill-in arrow; tapping opens its choices. */
export const Row = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  min-height: 56px;
  padding: ${SPACE[8]} ${SPACE[12]} ${SPACE[8]} ${SPACE[16]};
  border: 0;
  border-radius: 14px;
  background: ${HOME.panel};
  color: ${HOME.t50};
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

export const RowName = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

export const RowValue = styled.span<{ $applied: boolean }>`
  margin-left: auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: ${(p) => (p.$applied ? ACQ.orange : HOME.t400)};
`;

/** Drilled into a filter: a back arrow and the filter's name in the sheet's header (the sheet keeps its close
 *  button), as the creatives editor does on a phone. */
export const BackHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-left: -6px; /* off-scale on purpose: the arrow's own inset lines it up with the sheet's edge */
  padding: 6px; /* off-scale on purpose: a 34px hit area around the 22px arrow */
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.text};
  cursor: pointer;
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.textBright};
    background: ${COLORS.surfaceMuted};
  }
`;

/** The drilled-in filter's name, in the sheet title's gradient (a translated name, so plain text, not <Text>). */
export const HeaderTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

/* ── a filter's choices (the pane, or the phone's drilled-in view) ───────────────────────────────────────── */

export const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

/** A faded hairline between groups of choices (the owner's own links, then the affiliates). */
export const OptionsRule = styled(Divider)`
  margin: ${SPACE[4]} ${SPACE[12]};
`;

/** One choice: its name, its visitors at the right, a check when it is the current one. */
export const Option = styled.button<{ $active: boolean; $nested: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  min-height: 44px;
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[4]} ${(p) => (p.$nested ? SPACE[32] : SPACE[12])};
  border: 0;
  border-radius: 10px;
  background: ${(p) => (p.$active ? HOME.menuActiveBg : 'transparent')};
  color: ${(p) => (p.$active ? HOME.t50 : HOME.t400)};
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
`;

export const OptionMeta = styled.span`
  margin-left: auto;
  min-width: 28px;
  display: inline-flex;
  justify-content: flex-end;
  color: ${HOME.t500};
  font-variant-numeric: tabular-nums;
`;

/** The check's (or checkbox's) slot: kept on every row, so the names and counts line up. */
export const Check = styled.span`
  width: 18px;
  display: inline-flex;
  flex: none;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
`;

/** "Clear all": a text button in the studio's danger red (it resets every filter in the sheet). */
export const ClearButton = styled.button`
  height: 44px;
  padding: ${SPACE[0]} ${SPACE[16]};
  border: 0;
  border-radius: 100px;
  background: transparent;
  color: ${COLORS.danger};
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${HOME.ease};

  &:hover,
  &:focus-visible {
    background: ${COLORS.dangerSurface};
  }
`;

export const Empty = styled.span`
  display: block;
  padding: ${SPACE[8]} ${SPACE[12]} ${SPACE[12]};
  font-size: 13px;
  color: ${HOME.t500};
`;

/** A choice's placeholder while the section's choices load: an option row's shape (its check slot, picture, name and
 *  count), not clickable. */
export const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  min-height: 44px;
  /* The check's 18px slot plus the row's gap, so the placeholders start where the names do. */
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[4]} calc(${SPACE[12]} + 18px + ${SPACE[12]});

  & > :last-child {
    margin-left: auto;
  }
`;

export const OptionIcon = styled.span`
  display: inline-flex;
  flex: none;
  margin-right: -4px;
`;

/** A choice's glyph on the Website tab's dim disc (GlyphBadge's), at a choice row's size. */
export const GlyphDisc = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${WEBSITE_ANALYTICS.glyphBg};

  svg {
    display: block;
  }
`;

/** The sheet's body as one element, so the trackers' opt-outs mark all of it; it draws no box, so the body lays out as
 *  before. */
export const OptOutScope = styled.div`
  display: contents;
`;
